/**
 * FairCorp products — single source for Home (featured carousel) and Our Work.
 * Longer `details` are taken from the product pages and the published terms.
 * No new projects, clients, metrics, or screenshots.
 *
 * `serviceId` ties a product to the monitored-service status feed so a disabled
 * service shows "Temporarily unavailable" and routes to /outage.
 */
import { HOSTING_URL } from '../lib/site';

export type ProductStatus = 'development' | 'active';

export interface Product {
  id: string;
  name: string;
  summary: string;
  /** Longer description already published elsewhere in this site. */
  details: string[];
  link: string;
  external?: boolean;
  cta: string;
  logo: string;
  status: ProductStatus;
  serviceId: string;
}

export const PRODUCTS: Product[] = [
  {
    id: 'hosting',
    name: 'FairCorp Hosting',
    summary: 'The FairCorp hosting directory, published on its own site.',
    details: [
      'The terms of service name the FairCorp hosting directory as one of the FairCorp websites and services, alongside this hub and the ToolBox storefront.',
      'Web hosting is handled on that specialized site. This entry is the link to it. Catalog details stay on that site.',
    ],
    link: HOSTING_URL,
    external: true,
    cta: 'Open Hosting',
    logo: '/FinalLogoRebuild.png',
    status: 'active',
    serviceId: 'hosting',
  },
  {
    id: 'toolbox',
    name: 'FairCorp ToolBox',
    summary: 'Commercial software storefront and staff portal: browse and purchase licensed software.',
    details: [
      'ToolBox is the FairCorp storefront. People browse and purchase licensed software there, and staff use the portal alongside that storefront.',
      'The terms of service name the FairCorp ToolBox storefront as one of the sites software is distributed through, together with this hub and the hosting directory.',
    ],
    link: 'https://toolbox.fair-corp.com',
    external: true,
    cta: 'Open ToolBox',
    logo: '/mono-toolbox.svg',
    status: 'active',
    serviceId: 'toolbox',
  },
  {
    id: 'cloud',
    name: 'FairCorp Cloud',
    summary: 'Secure, affordable cloud storage with an easy interface, accessible anywhere.',
    details: [
      'FairCorp Cloud is our secure and affordable cloud storage system with guaranteed file safety. It is built around enterprise-grade security and an easy-to-use interface, so files stay protected and can be reached from anywhere.',
      'Secure file storage uses enterprise-grade encryption and access controls to keep files safe and private.',
      'File management is an intuitive interface for uploading, organizing, and sharing files.',
      'Access anywhere means the files are available from any device through the responsive web app.',
      'The Cloud page describes affordable pricing as competitive storage for individuals and businesses of all sizes, and it currently marks the product as deploying soon.',
    ],
    link: '/cloud',
    cta: 'Learn more',
    logo: '/mono-faircorp-cloud.png',
    status: 'development',
    serviceId: 'cloud',
  },
  {
    id: 'stockbot',
    name: 'StockBot AI',
    summary: 'The first AI built fully by FairCorp: real-time buy, sell, or hold suggestions for new investors.',
    details: [
      'The first AI built fully by FairCorp, designed to guide new investors with real-time suggestions on stocks to buy, sell, or hold.',
      'The product page describes intelligent investment recommendations powered by advanced market analysis, and it currently shows the service as coming soon.',
    ],
    link: '/stockbot',
    cta: 'Learn more',
    logo: '/mono-stockbot-ai.png',
    status: 'development',
    serviceId: 'stockbot',
  },
  {
    id: 'investor',
    name: 'Investor Outreach',
    summary: 'A network connecting people on investment and entrepreneurship journeys with experienced mentors.',
    details: [
      'A social networking platform connecting individuals on their investment and entrepreneurship journeys with experienced professionals and mentors.',
      'The product page describes building connections and learning through that community, and it currently shows the service as coming soon.',
    ],
    link: '/investor',
    cta: 'Learn more',
    logo: '/mono-investor-outreach.png',
    status: 'development',
    serviceId: 'investor',
  },
  {
    id: 'audit',
    name: 'Freelance Audit System',
    summary: 'Audit and compliance management for freelancers and small businesses to organize finances and operations.',
    details: [
      'Professional audit and compliance management platform for freelance professionals and small businesses.',
      'The system is designed to help freelancers and small businesses manage their finances and operations. The product page currently shows the service as coming soon.',
    ],
    link: '/audit',
    cta: 'Learn more',
    logo: '/mono-freelance-audit.png',
    status: 'development',
    serviceId: 'audit',
  },
];

export const STATUS_LABEL: Record<ProductStatus, string> = {
  active: 'Live',
  development: 'In development',
};
